import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import LockIcon from "@material-ui/icons/Lock";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  hint: {
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(0.75),
  },
  fixedRow: {
    opacity: 0.7,
    backgroundColor: theme.palette.action.hover,
  },
  position: {
    minWidth: 22,
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    textAlign: "right",
  },
  name: {
    flex: 1,
    minWidth: 0,
  },
  tagChip: {
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
  },
  empty: {
    padding: theme.spacing(3, 1),
    textAlign: "center",
    color: theme.palette.text.disabled,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const KanbanOrderModal = ({ open, onClose, reload }) => {
  const classes = useStyles();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchTags = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tags/kanban");
        setTags(data.lista || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, [open]);

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= tags.length) return;

    setTags(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/tags/kanban/order", { tagIds: tags.map(tag => tag.id) });
      toast.success("Ordem das raias atualizada.");
      if (reload) reload();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle>Ordenar raias do Kanban</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" className={classes.hint}>
          Define a ordem em que as colunas aparecem no Kanban. Vale para todos
          os usuários da empresa.
        </Typography>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <Box className={`${classes.row} ${classes.fixedRow}`}>
              <Typography className={classes.position}>1</Typography>
              <Box className={classes.name}>
                <Typography variant="body2">Sem categoria</Typography>
              </Box>
              <Tooltip title="Esta raia recebe todo ticket sem tag Kanban e fica sempre em primeiro">
                <LockIcon fontSize="small" color="disabled" />
              </Tooltip>
            </Box>

            {tags.length === 0 ? (
              <Typography variant="body2" className={classes.empty}>
                Nenhuma tag habilitada no Kanban.
              </Typography>
            ) : (
              tags.map((tag, index) => (
                <Box key={tag.id} className={classes.row}>
                  <Typography className={classes.position}>
                    {index + 2}
                  </Typography>
                  <Box className={classes.name}>
                    <Chip
                      className={classes.tagChip}
                      style={{ backgroundColor: tag.color }}
                      label={tag.name}
                      size="small"
                    />
                  </Box>
                  <Tooltip title="Mover para cima">
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === 0 || saving}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Mover para baixo">
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === tags.length - 1 || saving}
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || saving || tags.length === 0}
        >
          {saving ? <CircularProgress size={20} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanOrderModal;
